import { useState, useEffect, useRef } from 'react'
import { Routes, Route, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import './App.css'

// Cyberpunk city data
const DISTRICTS = [
  { id: 'neo-downtown', name: 'NEO DOWNTOWN', threat: 'MODERATE', description: 'Corporate towers pierce the neon-lit sky, while street vendors and hackers mingle in the shadows below.' },
  { id: 'eastside-slums', name: 'EASTSIDE SLUMS', threat: 'HIGH', description: 'Forgotten by the corps, ruled by gangs. The perfect place to disappear—or get disappeared.' },
  { id: 'corp-plaza', name: 'CORPORATE PLAZA', threat: 'LOW', description: 'Pristine and heavily monitored. The elite live in artificial paradise while private security ensures no undesirables enter.' },
  { id: 'tech-market', name: 'TECH MARKET', threat: 'MODERATE', description: 'Black market implants, custom tech, and information—all for sale if you can pay the price.' },
  { id: 'neon-gardens', name: 'NEON GARDENS', threat: 'VARIABLE', description: 'Entertainment district where reality blurs with virtual fantasies. Pleasure and danger in equal measure.' },
];

// Cybernetic upgrades
const UPGRADES = [
  { id: 'neural-link', name: 'NEURAL LINK', price: 7500, effect: 'Enhanced cognitive processing and direct neural interface with compatible tech.' },
  { id: 'optic-enhance', name: 'OPTIC ENHANCEMENT', price: 4200, effect: 'Night vision, thermal imaging, and augmented reality overlay with tactical data.' },
  { id: 'reflex-booster', name: 'REFLEX BOOSTER', price: 6800, effect: 'Dramatically improved reaction time and coordination.' },
  { id: 'dermal-armor', name: 'DERMAL ARMOR', price: 5500, effect: 'Subdermal plating provides protection against ballistic and blade weapons.' },
  { id: 'cyber-limb', name: 'CYBER LIMB', price: 12000, effect: 'Replacement limb with enhanced strength, hidden compartments, and optional weapon mounts.' },
];

// News feed data
const NEWS_FEED = [
  { id: 1, title: 'CORPORATE WAR LOOMS', content: 'Tensions between Arasaka and Militech escalate after failed negotiations.' },
  { id: 2, title: 'BLACKOUT IN SECTOR 7', content: 'Third power grid failure this month. Corps blame hackers, locals blame corps.' },
  { id: 3, title: 'NEW SYNTHETIC DRUG HITS STREETS', content: 'Authorities warn of "Chrome Burn" causing hallucinations and violent behavior.' },
  { id: 4, title: 'AI RIGHTS PROTEST TURNS VIOLENT', content: 'Self-aware AIs and supporters clash with corporate security forces.' },
  { id: 5, title: 'WEATHER CONTROL MALFUNCTION', content: 'Acid rain advisory in effect for eastern districts until further notice.' },
];

// Terminal component with typing effect
interface TerminalProps {
  text: string;
  speed?: number;
}

const Terminal: React.FC<TerminalProps> = ({ text, speed = 50 }) => {
  const [displayText, setDisplayText] = useState('');
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (index < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text.charAt(index));
        setIndex(index + 1);
      }, speed);

      return () => clearTimeout(timeout);
    }
  }, [index, text, speed]);

  return (
    <div className="cyber-terminal">
      <div className="terminal-header">
        <span className="terminal-dot"></span>
        <span className="terminal-dot"></span>
        <span className="terminal-dot"></span>
        <span className="terminal-title">NEURAL.LINK//CONNECTED</span>
      </div>
      <div className="terminal-content">
        <span className="terminal-prompt">&gt;</span>
        <span className="terminal-text">{displayText}</span>
        <span className="terminal-cursor">_</span>
      </div>
    </div>
  );
};

// Interactive map component
interface CityMapProps {
  onSelectDistrict: (district: typeof DISTRICTS[0]) => void;
}

const CityMap: React.FC<CityMapProps> = ({ onSelectDistrict }) => {
  return (
    <div className="cyber-map">
      <div className="map-grid">
        {DISTRICTS.map((district) => (
          <motion.div
            key={district.id}
            className={`map-district ${district.id}`}
            whileHover={{ scale: 1.05, boxShadow: '0 0 15px var(--color-neon-blue)' }}
            onClick={() => onSelectDistrict(district)}
          >
            <h3>{district.name}</h3>
            <div className="threat-level">
              <span>THREAT:</span>
              <span className={`threat-${district.threat.toLowerCase()}`}>{district.threat}</span>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="map-overlay"></div>
    </div>
  );
};

// Home page
const Home = () => {
  const [credits, setCredits] = useState(10000);
  const [selectedDistrict, setSelectedDistrict] = useState<typeof DISTRICTS[0] | null>(null);
  const [showTerminal, setShowTerminal] = useState(true);

  useEffect(() => {
    // Hide terminal after intro
    const timer = setTimeout(() => {
      setShowTerminal(false);
    }, 6000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {showTerminal ? (
        <Terminal text="INITIALIZING NEURAL INTERFACE... WELCOME TO NIGHT CITY 2077. CONNECTING TO NETWORK..." />
      ) : (
        <>
          <div className="cyber-hero">
            <h1 className="glitch" data-text="NIGHT CITY 2077">NIGHT CITY 2077</h1>
            <p>NAVIGATE THE NEON JUNGLE. SURVIVE THE CONCRETE WASTELAND.</p>
          </div>

          <div className="cyber-container">
            <div className="status-bar">
              <div className="status-item">
                <span className="status-label">USER:</span>
                <span className="status-value neon-text">V</span>
              </div>
              <div className="status-item">
                <span className="status-label">CREDITS:</span>
                <span className="status-value neon-text">{credits.toLocaleString()}</span>
              </div>
              <div className="status-item">
                <span className="status-label">STATUS:</span>
                <span className="status-value neon-text-pink">CONNECTED</span>
              </div>
            </div>

            <div className="cyber-grid">
              <div className="cyber-card main-card">
                <h2 className="neon-text">CITY MAP</h2>
                <p>Select a district to explore</p>
                <CityMap onSelectDistrict={setSelectedDistrict} />
              </div>

              <div className="cyber-card">
                <h2 className="neon-text-pink">NEWS FEED</h2>
                <div className="news-feed">
                  {NEWS_FEED.map(item => (
                    <motion.div
                      key={item.id}
                      className="news-item"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: item.id * 0.1 }}
                    >
                      <h3>{item.title}</h3>
                      <p>{item.content}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {selectedDistrict && (
              <motion.div
                className="district-detail cyber-card"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="district-header">
                  <h2 className="neon-text">{selectedDistrict.name}</h2>
                  <span className={`threat-${selectedDistrict.threat.toLowerCase()}`}>
                    THREAT LEVEL: {selectedDistrict.threat}
                  </span>
                </div>
                <p>{selectedDistrict.description}</p>
                <div className="district-actions">
                  <Link to={`/district/${selectedDistrict.id}`}>
                    <button>EXPLORE</button>
                  </Link>
                  <button className="cyber-btn-secondary" onClick={() => setSelectedDistrict(null)}>CLOSE</button>
                </div>
              </motion.div>
            )}

            <div className="card">
              <h2 className="neon-text">CYBERNETIC UPGRADES</h2>
              <p>Enhance your capabilities with the latest tech</p>
              <div className="upgrades-list">
                {UPGRADES.map(upgrade => (
                  <motion.div
                    key={upgrade.id}
                    className="upgrade-item"
                    whileHover={{ x: 5 }}
                  >
                    <div className="upgrade-info">
                      <h3>{upgrade.name}</h3>
                      <p>{upgrade.effect}</p>
                    </div>
                    <div className="upgrade-price">
                      <span>{upgrade.price.toLocaleString()} €$</span>
                      <button
                        disabled={credits < upgrade.price}
                        onClick={() => {
                          if (credits >= upgrade.price) {
                            setCredits(credits - upgrade.price);
                            // Show purchase notification
                            alert(`${upgrade.name} purchased and installed!`);
                          }
                        }}
                      >
                        {credits >= upgrade.price ? 'PURCHASE' : 'INSUFFICIENT FUNDS'}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
              <Link to="/market">
                <button className="cyber-btn-secondary" style={{ marginTop: '20px' }}>VISIT BLACK MARKET</button>
              </Link>
            </div>
          </div>
        </>
      )}
    </motion.div>
  )
}

// Market page
const Market = () => {
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="cyber-container"
    >
      <h1 className="neon-text">BLACK MARKET</h1>

      <div className="cyber-card">
        <div className="market-search">
          <input
            type="text"
            placeholder="SEARCH ILLEGAL TECH..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cyber-input"
          />
          <button>SCAN</button>
        </div>

        <div className="market-warning">
          <p className="glitch" data-text="WARNING: UNAUTHORIZED ACCESS DETECTED">WARNING: UNAUTHORIZED ACCESS DETECTED</p>
          <p>This market contains illegal technology and is under surveillance by corporate security.</p>
          <p>Proceed at your own risk. Connection anonymized.</p>
        </div>

        <div className="market-items">
          <div className="market-item">
            <h3>MILITARY GRADE NEURAL IMPLANT</h3>
            <p>Restricted tech. Enhanced combat capabilities and tactical overlay.</p>
            <div className="item-details">
              <span className="item-price">32,000 €$</span>
              <span className="item-risk high-risk">RISK: HIGH</span>
            </div>
            <button>CONTACT DEALER</button>
          </div>

          <div className="market-item">
            <h3>MEMORY WIPE SERVICE</h3>
            <p>Selective or complete memory erasure. No questions asked.</p>
            <div className="item-details">
              <span className="item-price">15,000 €$</span>
              <span className="item-risk medium-risk">RISK: MEDIUM</span>
            </div>
            <button>CONTACT DEALER</button>
          </div>

          <div className="market-item">
            <h3>CORPORATE DATABASE ACCESS</h3>
            <p>24-hour access to Arasaka internal network. Non-traceable.</p>
            <div className="item-details">
              <span className="item-price">45,000 €$</span>
              <span className="item-risk extreme-risk">RISK: EXTREME</span>
            </div>
            <button>CONTACT DEALER</button>
          </div>
        </div>

        <Link to="/">
          <button style={{ marginTop: '20px' }}>EXIT MARKET</button>
        </Link>
      </div>
    </motion.div>
  )
}

// 404 page
const NotFound = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="cyber-container"
      style={{ textAlign: 'center' }}
    >
      <h1 className="glitch" data-text="404 ERROR">404 ERROR</h1>
      <div className="cyber-card">
        <h2 className="neon-text-pink">NEURAL LINK FAILURE</h2>
        <p>Connection lost. Data stream corrupted.</p>
        <p>Possible causes: Network interference, illegal access attempt, or neural damage.</p>
        <Link to="/">
          <button style={{ marginTop: '20px' }}>REBOOT SYSTEM</button>
        </Link>
      </div>
    </motion.div>
  )
}

function App() {
  // Audio effects
  const ambientAudioRef = useRef<HTMLAudioElement | null>(null);

  // Cyberpunk terminal effect
  useEffect(() => {
    console.log('%c NIGHT CITY OS v2.77.1 INITIALIZED ', 'background: #000; color: #00f0ff; font-size: 20px; font-weight: bold; text-shadow: 0 0 10px #00f0ff;');

    // Create ambient sound
    const audio = new Audio('https://freesound.org/data/previews/385/385897_7124421-lq.mp3');
    audio.volume = 0.2;
    audio.loop = true;
    ambientAudioRef.current = audio;

    // Uncomment to enable audio
    // audio.play().catch(e => console.log('Audio autoplay prevented:', e));

    return () => {
      if (ambientAudioRef.current) {
        ambientAudioRef.current.pause();
      }
    };
  }, []);

  return (
    <>
      <header className="cyber-header">
        <div className="cyber-logo">
          <h2 className="neon-text">NIGHT<span className="neon-text-pink">CITY</span></h2>
        </div>
        <nav className="cyber-nav">
          <Link to="/">HOME</Link>
          <Link to="/market">BLACK MARKET</Link>
          <button
            className="sound-toggle"
            onClick={() => {
              if (ambientAudioRef.current) {
                if (ambientAudioRef.current.paused) {
                  ambientAudioRef.current.play();
                } else {
                  ambientAudioRef.current.pause();
                }
              }
            }}
          >
            SOUND
          </button>
        </nav>
      </header>

      <AnimatePresence mode="wait">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/market" element={<Market />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AnimatePresence>

      <footer className="cyber-footer">
        <p>&copy; {new Date().getFullYear()} NIGHT CITY NEURAL NETWORK | CONNECTION SECURE</p>
      </footer>
    </>
  )
}

export default App